import React, { useState, useEffect } from 'react';
import { trackStepCompletion, trackConfigurationChange, startStepTimer, trackStepTiming, getSizeCategory } from '../services/analytics';

function SizeConfigurator({ dimensions, selectedType, shadeConfiguration, onUpdate }) {
  const [localDimensions, setLocalDimensions] = useState(dimensions);
  const [hasTrackedCompletion, setHasTrackedCompletion] = useState(false);

  // Track step 2 completion when dimensions are valid
  useEffect(() => {
    if (dimensions.width && dimensions.height && !hasTrackedCompletion) {
      trackStepTiming(2);
      trackStepCompletion(2, {
        width: dimensions.width,
        height: dimensions.height,
        shade_type: selectedType,
        configuration: shadeConfiguration,
        size_category: getSizeCategory(dimensions),
        area_sq_inches: dimensions.width * dimensions.height
      });
      startStepTimer(3);
      setHasTrackedCompletion(true);
    }
  }, [dimensions, selectedType, shadeConfiguration, hasTrackedCompletion]);

  const handleInputChange = (dimension, inputValue) => {
    // Allow empty string for clearing the input
    if (inputValue === '') {
      const newDimensions = { ...localDimensions, [dimension]: '' };
      setLocalDimensions(newDimensions);
      return;
    }
    
    const value = parseFloat(inputValue);
    if (!isNaN(value)) {
      // Just update the local state while typing, don't clamp yet
      const newDimensions = { ...localDimensions, [dimension]: value };
      setLocalDimensions(newDimensions);
    }
  };

  const handleInputBlur = (dimension) => {
    const value = parseFloat(localDimensions[dimension]);
    if (!isNaN(value)) {
      const minWidth = selectedType === 'pleated' ? 8 : 14;
      const maxWidth = selectedType === 'pleated' ? 76 : 80;
      const maxHeight = selectedType === 'pleated' ? 60 : 65;
      const constraints = dimension === 'width'
        ? { min: minWidth, max: maxWidth }
        : { min: 12, max: maxHeight };
      
      const clampedValue = Math.max(constraints.min, Math.min(constraints.max, value));
      // Round to nearest 0.25 (quarter inch)
      const roundedValue = Math.round(clampedValue * 4) / 4;
      
      // Track dimension change if value changed
      if (dimensions[dimension] !== roundedValue) {
        trackConfigurationChange(dimension, dimensions[dimension], roundedValue, 2);
      }
      
      const newDimensions = { ...localDimensions, [dimension]: roundedValue };
      setLocalDimensions(newDimensions);
      onUpdate({ dimensions: newDimensions });
    } else {
      // If invalid, reset to previous valid value
      const newDimensions = { ...localDimensions, [dimension]: dimensions[dimension] };
      setLocalDimensions(newDimensions);
    }
  };

  // Calculate SVG dimensions - scale down for display
  const maxDisplayWidth = 300;
  const maxDisplayHeight = 250;
  const widthScale = maxDisplayWidth / 80;
  const heightScale = maxDisplayHeight / 65;
  const scale = Math.min(widthScale, heightScale);
  
  const svgWidth = localDimensions.width * scale;
  const svgHeight = localDimensions.height * scale;

  // Determine if this is a roller shade (valu-line or premium)
  const isRollerShade = selectedType === 'valu-line' || selectedType === 'premium';
  const isDayNight = shadeConfiguration === 'day-night';

  return (
    <div>
      {/* Size Input Row */}
      <div className="tw-mb-8">
        <p className="tw-text-sm tw-text-gray-600 tw-mb-3">Enter dimensions in inches (min: {selectedType === 'pleated' ? '8' : '14'}"W x 12"H, max: {selectedType === 'pleated' ? '76"W x 60' : '80"W x 65'}"H)</p>
        <div className="tw-flex tw-items-center tw-justify-center tw-space-x-4">
          <div className="tw-flex tw-items-center">
            <label className="tw-text-sm tw-font-medium tw-mr-2">Width:</label>
            <input
              type="number"
              value={localDimensions.width}
              onChange={(e) => handleInputChange('width', e.target.value)}
              onBlur={() => handleInputBlur('width')}
              step="0.25"
              min={selectedType === 'pleated' ? 8 : 14}
              max={selectedType === 'pleated' ? 76 : 80}
              className="tw-w-20 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-center focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-red"
            />
          </div>

          <span className="tw-text-gray-400 tw-text-xl">×</span>
          
          <div className="tw-flex tw-items-center">
            <label className="tw-text-sm tw-font-medium tw-mr-2">Height:</label>
            <input
              type="number"
              value={localDimensions.height}
              onChange={(e) => handleInputChange('height', e.target.value)}
              onBlur={() => handleInputBlur('height')}
              step="0.25"
              min="12"
              max={selectedType === 'pleated' ? 60 : 65}
              className="tw-w-20 tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-center focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-red"
            />
          </div>
        </div>
      </div>

      {/* Visual Preview */}
      <div className="tw-bg-gray-100 tw-rounded-lg tw-p-6">
        <h3 className="tw-text-sm tw-font-medium tw-text-gray-600 tw-mb-4 tw-text-center">
          Shade Preview
        </h3>
        
        {/* SVG Shade Preview */}
        <div className="tw-flex tw-justify-center">
          <svg 
            width={svgWidth + 60} 
            height={svgHeight + 60}
            className="tw-transition-all tw-duration-300"
          >
            {/* Top Rail / Roller */}
            {isRollerShade ? (
              <g>
                {/* Right end cap, shown first so it is visually behind the roller tube */}
                <circle cx={28 + svgWidth} cy="25" r="10" fill="#999" />
                {/* Roller tube */}
                <rect
                  x="28"
                  y="15"
                  width={svgWidth + 4}
                  height="20"
                  fill="#C0C0C0"
                  stroke="#999"
                  strokeWidth="1"
                  rx="10"
                  ry="10"
                />
                {/* Left end cap */}
                <circle cx="32" cy="25" r="10" fill="#999" />
                
              </g>
            ) : (
              /* Standard top rail for pleated */
              <rect
                x="30"
                y="20"
                width={svgWidth}
                height="15"
                fill="#B0B0B0"
                stroke="#999"
                strokeWidth="1"
              />
            )}
            
            {/* Shade Body */}
            <g>
              {isDayNight ? (
                /* Day & Night Configuration */
                <>
                  {/* Night shade (top portion - darker) */}
                  <rect
                    x="30"
                    y="35"
                    width={svgWidth}
                    height={svgHeight / 2}
                    fill="#4A4A4A"
                    stroke="#333"
                    strokeWidth="1"
                  />
                  
                  {/* Day shade (bottom portion - lighter) */}
                  <rect
                    x="30"
                    y={35 + svgHeight / 2}
                    width={svgWidth}
                    height={svgHeight / 2}
                    fill="#F0F0F0"
                    stroke="#DDD"
                    strokeWidth="1"
                  />
                  
                  {/* Divider bar between day and night */}
                  <rect
                    x="30"
                    y={33 + svgHeight / 2}
                    width={svgWidth}
                    height="4"
                    fill="#888"
                    stroke="#666"
                    strokeWidth="1"
                  />
                </>
              ) : (
                /* Night Only Configuration */
                <rect
                  x="30"
                  y="35"
                  width={svgWidth}
                  height={svgHeight}
                  fill="#6B6B6B"
                  stroke="#555"
                  strokeWidth="1"
                />
              )}
              
              {/* Add pleats only for pleated type */}
              {selectedType === 'pleated' && (
                <>
                  {isDayNight ? (
                    /* Pleats for day & night */
                    <>
                      {/* Night shade pleats */}
                      {Array.from({ length: Math.floor(svgHeight / 30) }, (_, i) => (
                        <line
                          key={`night-${i}`}
                          x1="30"
                          y1={35 + (i + 1) * 15}
                          x2={30 + svgWidth}
                          y2={35 + (i + 1) * 15}
                          stroke="#3A3A3A"
                          strokeWidth="1"
                          opacity="0.5"
                        />
                      ))}
                      {/* Day shade pleats */}
                      {Array.from({ length: Math.floor(svgHeight / 30) }, (_, i) => (
                        <line
                          key={`day-${i}`}
                          x1="30"
                          y1={35 + svgHeight / 2 + (i + 1) * 15}
                          x2={30 + svgWidth}
                          y2={35 + svgHeight / 2 + (i + 1) * 15}
                          stroke="#D0D0D0"
                          strokeWidth="1"
                          opacity="0.7"
                        />
                      ))}
                    </>
                  ) : (
                    /* Pleats for night only */
                    Array.from({ length: Math.floor(svgHeight / 15) }, (_, i) => (
                      <line
                        key={i}
                        x1="30"
                        y1={35 + (i + 1) * 15}
                        x2={30 + svgWidth}
                        y2={35 + (i + 1) * 15}
                        stroke="#5A5A5A"
                        strokeWidth="1"
                        opacity="0.5"
                      />
                    ))
                  )}
                </>
              )}
              
              {/* Side mounting points */}
              <circle cx="35" cy="27.5" r="2" fill="#666" />
              <circle cx={25 + svgWidth} cy="27.5" r="2" fill="#666" />
              
              {/* Pull cord for pleated shades */}
              {selectedType === 'pleated' && (
                <g>
                  <line
                    x1={30 + svgWidth - 10}
                    y1={35 + svgHeight}
                    x2={30 + svgWidth - 10}
                    y2={35 + svgHeight + 15}
                    stroke="#999"
                    strokeWidth="2"
                  />
                  <circle
                    cx={30 + svgWidth - 10}
                    cy={35 + svgHeight + 18}
                    r="3"
                    fill="#888"
                  />
                </g>
              )}
            </g>
            
            {/* Width dimension line and label */}
            <g>
              <line
                x1="30"
                y1={svgHeight + 45}
                x2={30 + svgWidth}
                y2={svgHeight + 45}
                stroke="#666"
                strokeWidth="1"
              />
              <line
                x1="30"
                y1={svgHeight + 42}
                x2="30"
                y2={svgHeight + 48}
                stroke="#666"
                strokeWidth="1"
              />
              <line
                x1={30 + svgWidth}
                y1={svgHeight + 42}
                x2={30 + svgWidth}
                y2={svgHeight + 48}
                stroke="#666"
                strokeWidth="1"
              />
              <text
                x={30 + svgWidth / 2}
                y={svgHeight + 58}
                textAnchor="middle"
                className="tw-text-xs tw-fill-gray-700"
              >
                {localDimensions.width}"
              </text>
            </g>
            
            {/* Height dimension line and label */}
            <g>
              <line
                x1="20"
                y1="35"
                x2="20"
                y2={35 + svgHeight}
                stroke="#666"
                strokeWidth="1"
              />
              <line
                x1="17"
                y1="35"
                x2="23"
                y2="35"
                stroke="#666"
                strokeWidth="1"
              />
              <line
                x1="17"
                y1={35 + svgHeight}
                x2="23"
                y2={35 + svgHeight}
                stroke="#666"
                strokeWidth="1"
              />
              <text
                x="10"
                y={35 + svgHeight / 2}
                textAnchor="middle"
                className="tw-text-xs tw-fill-gray-700"
                transform={`rotate(-90, 10, ${35 + svgHeight / 2})`}
              >
                {localDimensions.height}"
              </text>
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}

export default SizeConfigurator;